<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <b-button
            v-if="getUserPermission('CREATE_USER_ROLE')"
            variant="primary"
            @click="
              showModal('modal-lg'),
              (isReadonly = false),
              (modalType = { type: 'add', text: $t('Create New Role') })
            "
          >
            {{ $t("Add") }}
          </b-button>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-button
              variant="primary"
              class="mr-1"
              @click="showModal('modal-filter')"
            >
              <feather-icon icon="FilterIcon" /> {{ $t("Filter") }}
            </b-button>
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      :items="cachedAUserRolesList ? cachedAUserRolesList.data : roleList"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No data available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <!-- Column: Role Name -->
      <template #cell(roleName)="data">
        {{ data.value }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_USER_ROLE')"
              @click="updateViewRole(data.item, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t("Modify") }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="updateViewRole(data.item, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t("View") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      centered
      size="xl"
      :title="modalType.text"
      @hidden="clearForm"
    >
      <b-form @submit.prevent="handleSubmit">
        <validation-observer ref="roleRules">
          <b-row class="match-height">
            <!-- Role Name -->
            <b-col md="12">
              <InputField
                id="role-name"
                v-model="roleFormData.name"
                :label="$t('Role Name')"
                :placeholder="$t('Enter role name')"
                type="text"
                :error-name="$t('Role Name')"
                rules="required"
                for="role-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Role Name -->
            <!-- Role Description -->
            <b-col md="12">
              <TextAreaField
                id="role-description"
                v-model="roleFormData.description"
                rows="4"
                :label="$t('Role Description')"
                :placeholder="$t('Enter role description')"
                type="text"
                :error-name="$t('Role Description')"
                rules="required"
                for="role-description"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Role Description -->
          </b-row>
          <!-- Permission Selector -->
          <PermissionSelector
            ref="permissionSelector"
            v-model="roleFormData.permissions"
            :permissions="permissions"
            :readonly="isReadonly"
          />
        </validation-observer>
      </b-form>
      <template #modal-footer="{ cancel }">
        <div v-if="isReadonly" />
        <template v-else>
          <b-button
            variant="outline-secondary"
            :disabled="isLoading"
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="isLoading"
            @click="handleSubmit"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-50"
            />
            {{ modalType.buttonLabel }}
          </b-button>
        </template>
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="modal-filter"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="getRoles"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name  -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            for="name"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BModal,
  BForm,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import PermissionSelector from '@/views/components/permission/PermissionSelector.vue'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import TextAreaField from '../../forms/TextAreaField.vue'
import { adminPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    Button,
    BTable,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    TableEntries,
    ValidationObserver,
    InputField,
    TextAreaField,
    TablePagination,
    PermissionSelector,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      required,
      sortBy: 'id',
      isBusy: false,
      sortDesc: false,
      showSpinner: false,
      tableHeaders: [
        { key: 'SN', sortable: true, label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'description', sortable: true, label: 'Description' },
        { key: 'actions', label: 'Actions' },
      ],
      roleFormData: {
        name: '',
        description: '',
        permissions: [],
      },
      roleList: [],
      permissions: {},
      loading: false,
      modalType: {
        type: '',
        text: '',
        buttonLabel: this.$t('Save'),
      },
      filter: {
        name: '',
      },
      isReadonly: false,
    }
  },
  computed: {
    ...mapGetters(['generalLedger/generalLedgerList']),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedAUserRolesList() {
      return this.getTableCache('user-roles', this.cacheKey)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchUserRoles()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchUserRoles()
    },
  },
  mounted() {
    this.serveCacheAndFetchUserRoles()
    this.fetchAllPermission()
  },
  methods: {
    ...mapActions({
      getAllPermission: 'admin/getAllPermission',
      addRole: 'admin/addRole',
      editRole: 'admin/editRole',
      getAllRoles: 'admin/getRoles',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),

    async serveCacheAndFetchUserRoles() {
      const { cacheKey } = this
      const cache = this.getTableCache('user-roles', cacheKey)

      if (cache && cache.data) {
        this.roleList = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.getRoles(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.getRoles(false)
      }
    },
    toggleModal() {
      // We pass the ID of the button that we want to return focus to
      // when the modal has hidden
      this.$refs['my-modal'].toggle('#toggle-btn')
    },
    async getRoles(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        const response = await this.getAllRoles({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.VIEW_USER_ROLE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const cache = this.cachedAUserRolesList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.roleList = data.data
            this.totalCount = data.totalItems
            // If on page 1 and data changed, mark all role cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('user-roles')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'user-roles',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.totalItems,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) {
        bvModalEvt.preventDefault()
      }
      this.$refs.roleRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisRole = this.$t('this Role?')
          this.confirm(this.createUpdateRole, {
            text: `${areYouSure} ${this.modalType.type} ${thisRole}`,
          })
        }
      })
    },
    async createUpdateRole() {
      this.isLoading = true
      try {
        const response = this.modalType.type === 'add'
          ? await this.addRole(this.roleFormData)
          : await this.editRole({
            ...this.roleFormData,
            id: this.modalType.id,
          })
        if (response.status === 200) {
          const requestToText = this.$t('Your request to')
          const text = this.$t('Role was successful')
          this.alertSuccess({
            text: `${requestToText} ${this.modalType.type} ${this.roleFormData.name} ${text}`,
          })
          this.getRoles(true)
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Role failed. Reason')
          this.alertError({
            text: `${requestToText} ${this.modalType.type} ${reasonText}: ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isLoading = false
        this.toggleModal()
        this.clearForm()
      }
    },

    // Enhanced updateViewRole method
    updateViewRole(data, actionType) {
      if (actionType === 'view') {
        this.isReadonly = true
        this.modalType = {
          type: 'view',
          text: this.$t('View Role'),
          id: data.id,
        }
      } else {
        this.isReadonly = false
        this.modalType = {
          type: 'update',
          text: this.$t('Modify Role'),
          id: data.id,
          buttonLabel: this.$t('Update'),
        }
      }
      this.toggleModal()
      const { name, description, permissions } = data
      this.roleFormData = { name, description, permissions: permissions || [] }
    },

    async fetchAllPermission() {
      // this.isBusy = true
      try {
        const response = await this.getAllPermission({
          permission: adminPermissions.VIEW_USER_ROLE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.permissions = data.data.reduce((memo, curr) => {
            // eslint-disable-next-line no-param-reassign
            memo[curr.groupName] = Array.isArray(memo[curr.groupName])
              ? memo[curr.groupName].concat(curr)
              : [memo[curr.groupName]].concat(curr)
            return memo
          }, {})
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    clearForm() {
      this.roleFormData = {
        name: '',
        description: '',
        permissions: [],
      }
      // Clear search in PermissionSelector
      if (this.$refs.permissionSelector) {
        this.$refs.permissionSelector.clearSearch()
      }
    },

    resetFilters() {
      this.filter = { ...null }
      this.markTableCacheStale('user-roles')
    },
    resetFilter() {
      this.filter = { ...null }
      this.markTableCacheStale('user-roles')
      this.getRoles(true)
    },
  },
}
</script>

<style lang="scss" scoped>
.select-all {
  height: 50.3px;
}
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
.card-item {
  border: 1px solid #d8d6de !important;
  border-radius: 0.358rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
